import React, { useState } from "react";
import { CategoryTreeNodeDto } from "@semo/api-contracts";
import { Switch, ScrollArea, cn } from "@semo/shared-ui";
import { ChevronDown, Package } from "@semo/icons";

const ALL_ID = "1";
/** 상단 네비에 핀으로 노출할 대분류 개수 (나머지는 "전체 카테고리" LNB에서). */
const PINNED_TAB_COUNT = 4;

interface CategoryTopNavProps {
  categoryTree: CategoryTreeNodeDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  mode: "all" | "dealing";
  setMode: (mode: "all" | "dealing") => void;
}

/** 하위 카테고리 칩 (LNB 하단 단계) */
const SubcategoryChips: React.FC<{
  parent: CategoryTreeNodeDto;
  selectedId: string | null;
  onPick: (id: string) => void;
}> = ({ parent, selectedId, onPick }) => (
  <div className="flex flex-wrap gap-1.5">
    <button
      type="button"
      onClick={() => onPick(parent.id)}
      className={cn(
        "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
        selectedId === parent.id
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-muted-foreground border-border hover:border-primary/50",
      )}
    >
      전체
    </button>
    {parent.children.map((child) => (
      <button
        key={child.id}
        type="button"
        onClick={() => onPick(child.id)}
        className={cn(
          "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
          selectedId === child.id
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-background text-muted-foreground border-border hover:border-primary/50",
        )}
      >
        {child.name}
      </button>
    ))}
  </div>
);

/** 풀폭 LNB 패널 (내 취급만 → 대분류 그리드 → 하위 칩 단계) */
const LnbPanel: React.FC<{
  categoryTree: CategoryTreeNodeDto[];
  panelCatId: string | null;
  setPanelCatId: (id: string) => void;
  selectedId: string | null;
  onPick: (id: string) => void;
  onSelectCategory: (id: string) => void;
  mode: "all" | "dealing";
  setMode: (mode: "all" | "dealing") => void;
}> = ({
  categoryTree,
  panelCatId,
  setPanelCatId,
  selectedId,
  onPick,
  onSelectCategory,
  mode,
  setMode,
}) => {
  const panelCat = categoryTree.find((c) => c.id === panelCatId) ?? null;

  return (
    <div className="flex flex-col">
      {/* 상단: 타이틀 + 내 취급만 토글(강조) */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b bg-muted/20">
        <div className="flex items-center gap-1.5 font-semibold text-sm">
          <Package className="w-4 h-4 text-primary" />
          전체 카테고리
        </div>
        <label
          htmlFor="lnb-dealing"
          className={cn(
            "flex items-center gap-2.5 px-4 py-2 rounded-lg border cursor-pointer select-none transition-colors",
            mode === "dealing"
              ? "bg-primary/10 border-primary text-primary"
              : "bg-background border-border text-foreground hover:bg-accent",
          )}
        >
          <span className="text-sm font-semibold whitespace-nowrap">
            내 취급만 보기
          </span>
          <Switch
            id="lnb-dealing"
            checked={mode === "dealing"}
            onCheckedChange={(c) => setMode(c ? "dealing" : "all")}
          />
        </label>
      </div>

      {/* 대분류 그리드 (클릭 → 하단 하위 칩 노출) */}
      <ScrollArea className="max-h-[240px]">
        {/* 5열은 휴대폰에서 칸당 50px 남짓이라 이름이 전부 잘린다 — 폭에 따라 열을 줄인다. */}
        <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {categoryTree.map((cat) => {
            const isPanelActive = panelCatId === cat.id;
            const isSelected =
              selectedId === cat.id ||
              cat.children.some((ch) => ch.id === selectedId);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  setPanelCatId(cat.id);
                  onSelectCategory(cat.id);
                }}
                className={cn(
                  "text-sm px-3 py-2.5 rounded-md border text-center truncate transition-colors",
                  isPanelActive
                    ? "border-primary bg-primary/10 text-primary font-semibold"
                    : isSelected
                      ? "border-primary/40 bg-primary/5 text-primary"
                      : "bg-background border-border hover:bg-accent",
                )}
                title={cat.name}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </ScrollArea>

      {/* 하위 카테고리 칩 (대분류 클릭 후 노출) */}
      <div className="border-t px-4 py-3 bg-muted/10 min-h-[64px]">
        {panelCat ? (
          panelCat.children.length > 0 ? (
            <>
              <p className="text-2xs text-muted-foreground mb-2 font-medium">
                {panelCat.name} 하위 카테고리
              </p>
              <SubcategoryChips
                parent={panelCat}
                selectedId={selectedId}
                onPick={onPick}
              />
            </>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {panelCat.name} — 하위 카테고리가 없습니다.
              </p>
              <button
                type="button"
                onClick={() => onPick(panelCat.id)}
                className="px-3 py-1 rounded-full text-xs font-medium border border-primary bg-primary text-primary-foreground"
              >
                이 카테고리 보기
              </button>
            </div>
          )
        ) : (
          <p className="text-xs text-muted-foreground">
            대분류를 선택하면 하위 카테고리가 표시됩니다.
          </p>
        )}
      </div>
    </div>
  );
};

export const CategoryTopNav: React.FC<CategoryTopNavProps> = ({
  categoryTree,
  selectedId,
  onSelect,
  mode,
  setMode,
}) => {
  const [lnbOpen, setLnbOpen] = useState(false);
  // 패널에서 현재 펼쳐 보여줄 대분류 (하단 칩의 기준)
  const [panelCatId, setPanelCatId] = useState<string | null>(null);

  const pinnedCategories = categoryTree.slice(0, PINNED_TAB_COUNT);

  // 카테고리 선택 확정 → 패널 닫기
  const handlePick = (id: string) => {
    onSelect(id);
    setLnbOpen(false);
  };

  // 핀 탭/전체 카테고리 클릭 → 풀폭 LNB 토글 (해당 대분류 펼친 채로).
  // 대분류가 지정되면 그 대분류를 바로 선택(검색 칩 표시)하되 패널은 열어둠.
  const openLnbWith = (catId: string | null) => {
    if (lnbOpen && panelCatId === catId) {
      setLnbOpen(false);
      return;
    }
    setPanelCatId(catId);
    setLnbOpen(true);
    if (catId) onSelect(catId);
  };

  const tabBase =
    "flex items-center gap-0.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors";

  return (
    <div className="relative">
      {/* 네비 행 (스크림 위로) */}
      <div className="relative z-50 flex items-center gap-1 border-b bg-card px-3 py-1 sm:px-6 overflow-x-auto no-scrollbar">
        {/* 전체 = 풀폭 LNB(전 카테고리 그리드) 열기. 기본은 비활성, 눌러야 열림 */}
        <button
          type="button"
          onClick={() => openLnbWith(null)}
          className={cn(
            tabBase,
            lnbOpen && panelCatId === null
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-accent",
          )}
        >
          전체
          <ChevronDown
            className={cn(
              "w-3.5 h-3.5 opacity-60 transition-transform",
              lnbOpen && panelCatId === null && "rotate-180",
            )}
          />
        </button>

        {/* 자주 쓰는 대분류 핀 → 클릭 시 해당 대분류 펼친 LNB */}
        {pinnedCategories.map((cat) => {
          const isSelected =
            selectedId === cat.id ||
            cat.children.some((ch) => ch.id === selectedId);
          const isOpenTarget = lnbOpen && panelCatId === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => openLnbWith(cat.id)}
              className={cn(
                tabBase,
                isSelected || isOpenTarget
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-accent",
              )}
            >
              {cat.name}
              <ChevronDown
                className={cn(
                  "w-3.5 h-3.5 opacity-60 transition-transform",
                  isOpenTarget && "rotate-180",
                )}
              />
            </button>
          );
        })}
      </div>

      {/* 스크림 + 풀폭 LNB */}
      {lnbOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setLnbOpen(false)}
            aria-hidden
          />
          <div className="absolute left-0 right-0 top-full z-50 bg-card border-b shadow-xl">
            <LnbPanel
              categoryTree={categoryTree}
              panelCatId={panelCatId}
              setPanelCatId={setPanelCatId}
              selectedId={selectedId}
              onPick={handlePick}
              onSelectCategory={onSelect}
              mode={mode}
              setMode={setMode}
            />
          </div>
        </>
      )}
    </div>
  );
};
